import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Navigate, useNavigate } from "react-router-dom";
import apiClient, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { UserPlus, Key, Trash, ShieldStar, Users } from "@phosphor-icons/react";

const ROLES = ["admin", "manager", "submitter"];
const inputCls =
  "w-full h-11 px-3.5 rounded-md border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 focus:outline-none transition";

const roleBadge = {
  admin: "bg-slate-900 text-white",
  manager: "bg-blue-100 text-blue-700 border border-blue-200",
  submitter: "bg-slate-100 text-slate-600 border border-slate-200",
};

export default function UsersPage() {
  const { user, company, refreshCompany } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "submitter", password: "" });
  const [resetPw, setResetPw] = useState("");

  const load = () => {
    apiClient.get("/users").then((r) => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));
    refreshCompany().catch(() => {});
  };
  useEffect(load, []);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const seatLimit = company?.usage?.seat_limit ?? 2;
  const seatsUsed = users.length;
  const seatsFull = seatsUsed >= seatLimit;

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/users", form);
      toast.success("User created");
      setAddOpen(false);
      setForm({ email: "", name: "", role: "submitter", password: "" });
      load();
    } catch (err) {
      if (err.response?.status === 402) {
        const d = err.response.data?.detail || {};
        toast.error(d.message || "You've reached your team seat limit.");
        setAddOpen(false);
        navigate("/pricing");
        return;
      }
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create user");
    }
  };

  const changeRole = async (u, role) => {
    try {
      await apiClient.put(`/users/${u.id}`, { role });
      toast.success(`${u.name} is now ${role}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not change role");
      load();
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`/users/${resetFor.id}/reset-password`, { new_password: resetPw });
      toast.success(`Password reset for ${resetFor.name}`);
      setResetFor(null);
      setResetPw("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not reset password");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete user");
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-[#0F172A]">Manage Users</h1>
          <p className="text-slate-500 mt-2">Add reviewers, set roles, and reset passwords.</p>
          <div className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-md border border-slate-200 bg-white" data-testid="seat-usage">
            <Users size={15} weight="bold" className={seatsFull ? "text-red-600" : "text-blue-600"} />
            <span className="text-sm font-semibold text-[#0F172A]">{seatsUsed} / {seatLimit} team seats</span>
            {seatsFull && <button onClick={() => navigate("/pricing")} className="text-xs font-semibold text-blue-600 hover:underline ml-1">Upgrade</button>}
          </div>
        </div>
        <Dialog open={addOpen} onOpenChange={(o) => { if (o && seatsFull) { toast.error("You've reached your team seat limit. Upgrade to add more."); navigate("/pricing"); return; } setAddOpen(o); }}>
          <DialogTrigger asChild>
            <button data-testid="add-user-btn" disabled={seatsFull} className="inline-flex items-center gap-2 h-11 px-5 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm transition-colors disabled:opacity-50">
              <UserPlus size={17} weight="bold" /> Add User
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">Add User</DialogTitle></DialogHeader>
            <form onSubmit={addUser} className="space-y-4">
              <input data-testid="add-user-name" className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <input data-testid="add-user-email" type="email" className={inputCls} placeholder="name@proposalforensics.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              <select data-testid="add-user-role" className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <input data-testid="add-user-password" type="text" className={inputCls} placeholder="Temporary password (min 8 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
              <DialogFooter>
                <button data-testid="add-user-submit" type="submit" className="h-10 px-5 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm transition-colors">Create User</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                {["Name", "Email", "Role", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-400">Loading…</td></tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100">
                    <td className="px-4 py-3 font-semibold text-[#0F172A] whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        {u.role === "admin" && <ShieldStar size={15} weight="fill" className="text-slate-900" />}
                        {u.name}{u.id === user?.id && <span className="text-xs text-slate-400 font-normal">(you)</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{u.email}</td>
                    <td className="px-4 py-3">
                      <select
                        data-testid={`role-select-${u.id}`}
                        value={u.role}
                        onChange={(e) => changeRole(u, e.target.value)}
                        className={`h-8 rounded-md px-2 text-xs font-semibold ${roleBadge[u.role]}`}
                      >
                        {ROLES.map((r) => <option key={r} value={r} className="bg-white text-slate-900">{r}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button data-testid={`reset-pw-${u.id}`} onClick={() => setResetFor(u)} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors">
                          <Key size={14} /> Reset
                        </button>
                        <button data-testid={`delete-user-${u.id}`} onClick={() => removeUser(u)} disabled={u.id === user?.id} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-red-200 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                          <Trash size={14} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Reset Password — {resetFor?.name}</DialogTitle></DialogHeader>
          <form onSubmit={doReset} className="space-y-4">
            <input data-testid="reset-pw-input" type="text" className={inputCls} placeholder="New password (min 8 chars)" value={resetPw} onChange={(e) => setResetPw(e.target.value)} required />
            <DialogFooter>
              <button data-testid="reset-pw-submit" type="submit" className="h-10 px-5 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm transition-colors">Set New Password</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
